import { useIdle, useInterval } from '@mantine/hooks';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useCosmosRuntime } from 'providers/contexts/CosmosRuntimeContext';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { formatAuthorData } from 'utility/functions';
import { stringToColor } from 'utility/styles';
import { GetUserMeData } from 'utility/types/dataProvider';
import * as Y from 'yjs';
import {
  CollabProviderState,
  SyncedInitialYDocConfigState,
} from '../context/AssetConfigUtilities';

// --------------------------------------------------
// User Presence Tracking...
// --------------------------------------------------

/**
 * https://docs.yjs.dev/api/y.doc
 * https://docs.yjs.dev/api/about-awareness
 * https://docs.yjs.dev/getting-started/adding-awareness
 *
 * Notes:
 * event data comes from providers callbacks
 * onAwarenessChange / onAwarenessUpdate
 *
 * On occasion, have seen incoming awareness events
 * will drop down to just yourself (1 user) even
 * when we know others are connected. This seems
 * to be more common in the onAwarenessChange event
 * rather than the onAwarenessUpdate event. Maybe
 * is caused by the browser tab being backgrounded
 * or deprioritized for idle users? In most cases,
 * the next event afterwards will contain full data.
 *
 * Change events fires less often than the update
 * events, but both fire very rapidly and scales
 * up with number of users connected, so will need
 * to debounce/throttle listeners and calls to prevent
 * excessive spam updates and re-renders.
 *
 * Note we also should setup the awareness info
 * prior to setting up the provider or else the newly
 * connected user, on the first update will appear with
 * just a clientId and no user info, until the collab extensions
 * register user presence info, which can be an issue
 * if doing any initial update checks...
 *
 * The "data" looks like this as forwarded
 * by what onAwarenessUpdate provides:
 * [
 *   {
 *     "clientId": 584184061,
 *     "user": {
 *       "name": "Test User",
 *       "color": "#ABC123",
 *       "uuid": "402af360-1234-5678-9000-e723ad16e621",
 *       "email": "test@something.com",
 *       "avatar": "https://secure.gravatar.com/avatar/1234567890?d=mm&r=g"
 *     },
 *     "presence": {
 *       "uuid": "402af360-d81e-11ee-9fa7-e723ad16e621",
 *       "email": "test@something.com",
 *       "runtimeId": "runtime::01994e61-0c26-7049-bc30-2d4cc0539c0c",
 *       "isReadOnlyMode": false,
 *       "lastActiveIso": "2025-09-15T17:16:49.600Z"
 *     },
 *     "cursor": null
 *   },
 *   {
 *     "clientId": 487221366,
 *     "user": { "name": "another-user-test", ... },
 *     "presence": { ... }, ...
 *   }
 * ]
 *
 **/

type UseDocPresenceArgs = {
  yDoc?: Y.Doc;
  isReadOnlyMode?: boolean;
  collabProvider?: CollabProviderState;
  syncedInitialYDocConfig?: SyncedInitialYDocConfigState;
  /**
   * enable or disable automatic presence update effects,
   * will not disable manual update calls via returned functions
   */
  enableUpdates?: boolean;
};

type UserUuid = string;

type TiptapCursorData = { anchor: Y.RelativePosition; head: Y.RelativePosition } | null;

/**
 * Basic stable user info for collab extensions eg
 * CollaborationCursor, this data should mostly be
 * stable for the session and not change often.
 */
export type BuildCollabUserInfoArgs = {
  userInfo?: GetUserMeData;
};

export type CollabUserInfo = {
  name: string; // displayname via formatAuthorData
  color: string; // color via stringToColor
  avatar: string;
  uuid: string;
  email: string;
};

export function buildCollabUserInfo({
  userInfo,
}: BuildCollabUserInfoArgs): CollabUserInfo {
  const displayName = formatAuthorData(userInfo);
  const userName = displayName ?? 'Anonymous';
  const userColor = stringToColor(userName);
  const userEmail = userInfo?.email ?? '';
  const userUuid = userInfo?.uuid ?? '';
  const userAvatar = userInfo?.avatar ?? '';
  const result: CollabUserInfo = {
    name: displayName,
    color: userColor,
    uuid: userUuid,
    email: userEmail,
    avatar: userAvatar,
  };
  return result;
}

export type BuildCollabUserPresenceInfoArgs = {
  userInfo?: GetUserMeData;
  runtimeId?: string;
  isReadOnlyMode?: boolean | null;
  lastActiveIso?: string | null;
};

/**
 * Putting this under a separate key ('presence') as the tiptap
 * CollaborationCursor extension may overwrite the basic 'user' key
 * data with their own awareness updates, keep our own data separate
 * to not have to worry about it getting overwritten...
 */
export type CollabUserPresenceInfo = {
  uuid: CollabUserInfo['uuid'];
  email: CollabUserInfo['email'];
  runtimeId: string; // stable runtime id
  isReadOnlyMode: boolean | null; // session in readonly mode
  lastActiveIso: string | null; // last activity utc iso
};

export function buildCollabUserPresenceInfo({
  userInfo,
  runtimeId,
  isReadOnlyMode,
  lastActiveIso,
}: BuildCollabUserPresenceInfoArgs): CollabUserPresenceInfo {
  const result: CollabUserPresenceInfo = {
    uuid: userInfo?.uuid ?? '',
    email: userInfo?.email ?? '',
    runtimeId: runtimeId ?? '',
    isReadOnlyMode: isReadOnlyMode ?? null,
    lastActiveIso: lastActiveIso ?? null,
  };
  return result;
}

export type TiptapAwarenessInfo = {
  clientId?: number; // ydoc client id
  cursor?: TiptapCursorData | null; // null if no cursor field
  user?: CollabUserInfo; // user info for collab extensions
  presence?: CollabUserPresenceInfo; // our presence info key
};

/**
 * awareness.getLocalState() - can be null if unset or disconnected
 * Note: remote clients can mark you null if you are disconnected...
 */
type AwarenessStateType = TiptapAwarenessInfo | null;

export type UserPresenceMap = {
  [userUuid: UserUuid]: UserPresenceInfo;
};

export type UserSessionInfo = {
  runtimeId: CollabUserPresenceInfo['runtimeId'];
  isReadOnlyMode?: CollabUserPresenceInfo['isReadOnlyMode'];
  lastActiveIso?: CollabUserPresenceInfo['lastActiveIso'];
};

export type UserPresenceInfo = {
  uuid: CollabUserInfo['uuid'];
  email: CollabUserInfo['email'];
  avatar: CollabUserInfo['avatar'];
  name: CollabUserInfo['name'];
  color: CollabUserInfo['color'];
  sessions?: {
    [runtimeId: string]: UserSessionInfo;
  };
};

/**
 * Type thats closer to what is visible in console logs.
 * the original types: look for "StatesArray" / "onAwarenessUpdateParameters"
 * node_modules\@hocuspocus\provider\dist\packages\provider\src\types.d.ts
 */
export type AwarenessUpdateInfo = {
  states: TiptapAwarenessInfo[];
};

export type BuildPresenceMapArgs = {
  awUpdate?: AwarenessUpdateInfo;
  // Update userPresenceMap state as well?
  updateState?: boolean;
};

// Partial info of the last update this client tried to
// send from this hook, which can be used in some areas
// to work around any throttled presence ux updates.
type LastSentAwUpdate = {
  user: CollabUserInfo;
  presence: CollabUserPresenceInfo;
} | null;

type UseNxtDocPresenceRtn = {
  buildPresenceMap: ({
    awUpdate,
    updateState,
  }: BuildPresenceMapArgs) => Promise<UserPresenceMap>;
  removeSelfPresence: () => Promise<boolean>;
  updateSelfPresence: () => Promise<boolean>;
  lastSentUpdate: LastSentAwUpdate;
  userPresenceMap: UserPresenceMap;
  setUserPresenceMap: Dispatch<SetStateAction<UserPresenceMap>>;
};

export function useDocumentPresence({
  isReadOnlyMode,
  enableUpdates = true,
  collabProvider,
  syncedInitialYDocConfig,
}: UseDocPresenceArgs): UseNxtDocPresenceRtn {
  const { runtimeId } = useCosmosRuntime();
  const { userInfo } = useCosmos();

  /**
   * Note: although you can write to the ydoc before synced, it seems
   * unless you are fully synced and connected to provider, once you
   * connect those writes maybe cleared or reset by the cloud doc,
   * so recommended to only start auto updating presence data once
   * collab provider exists and have synced once fully...
   * eg: Boolean(collabProvider && syncedInitialYDocConfig && enableUpdates);
   */
  const isEnabled = Boolean(enableUpdates);

  // --------------------------------------------------
  // Handle Presence Updates
  // --------------------------------------------------

  /**
   * 60 sec idle activity timeout
   * 20 sec updates interval
   *
   * Note: rather than stopping updates while idle,
   * just continue to post updates, as this helps
   * with backgrounded / deprioritized tabs that can cause
   * idle users to flicker online/offline sometimes...
   * this can still occur if the browser throttles but
   * this interval helps attempt to mitigate it.
   *
   * Yjs awareness needs an update broadcast minimum every
   * 30 seconds or it will consider you offline and removed.
   * https://docs.yjs.dev/api/about-awareness
   *
   */
  const IDLE_TIMEOUT_MS = 1 * 60 * 1000;
  const INTERVAL_MS = 1 * 20 * 1000;

  // true if user does nothing for a given time
  const isIdle = useIdle(IDLE_TIMEOUT_MS, {
    events: ['mousemove', 'click', 'keydown', 'wheel', 'touchstart'],
    initialState: false,
  });

  // Track last presence update we tried to send (from this hook)
  const [lastSentUpdate, setLastSentUpdate] = useState<LastSentAwUpdate>(null);

  // Track update in progress via ref to avoid re-entry
  // if potentially called multiple times quickly.
  const isUpdatingRef = useRef(false);

  // Update self presence data in the ydoc awareness.
  const updateSelfPresence = useCallback(async () => {
    try {
      if (!collabProvider?.document) {
        throw new Error('Missing provider yDoc');
      }
      if (!collabProvider?.awareness) {
        throw new Error('Missing provider awareness');
      }
      if (!userInfo?.uuid) {
        throw new Error('Missing user uuid');
      }
      if (!runtimeId) {
        throw new Error('Missing runtimeId');
      }
      if (isUpdatingRef.current) {
        console.log('[updateSelfPresence]: Update in progress');
        return true;
      }

      // Mark updating in progress...
      isUpdatingRef.current = true;

      // Yjs awareness - update this users presence data
      // Only need to update user and presence awareness info...
      // we can let tiptap handle the rest (eg cursor, etc).
      const yAwareness = collabProvider.awareness;
      const inReadOnly = isReadOnlyMode ?? null;
      const prevAwData: AwarenessStateType = yAwareness.getLocalState();
      const prevTimestamp = prevAwData?.presence?.lastActiveIso ?? null;
      const nowIso = new Date().toISOString();

      // If user is idle use previous timestamp, else new one
      const newTs = isIdle ? prevTimestamp : nowIso;

      // Build updated user info and presence objects
      const updatedUserInfo = buildCollabUserInfo({
        userInfo: userInfo,
      });

      const updatedPresenceInfo = buildCollabUserPresenceInfo({
        userInfo: userInfo,
        runtimeId: runtimeId,
        isReadOnlyMode: inReadOnly,
        lastActiveIso: newTs,
      });

      const updatedAwState: LastSentAwUpdate = {
        user: updatedUserInfo,
        presence: updatedPresenceInfo,
      };

      // source code of setLocalStateField reads that
      // it only sets the field if the current awareness
      // getLocalState data exists, so if null, create it.
      if (prevAwData) {
        yAwareness.setLocalStateField('user', updatedAwState.user);
        yAwareness.setLocalStateField('presence', updatedAwState.presence);
      } else {
        yAwareness.setLocalState({
          user: updatedAwState.user,
          presence: updatedAwState.presence,
        });
      }

      // Update local last sent update state
      setLastSentUpdate(updatedAwState);

      return true;
    } catch (error) {
      console.error('[updateSelfPresence]:', error);
      return false;
    } finally {
      // Reset ref after attempt
      isUpdatingRef.current = false;
    }
  }, [
    isIdle,
    collabProvider?.awareness,
    collabProvider?.document,
    isReadOnlyMode,
    runtimeId,
    userInfo,
  ]);

  // Track self cleanup in progress via ref to avoid
  // re-entracy on hard/soft nav away and unmounts
  const isRemovingRef = useRef(false);
  // Remove self presence data in the ydoc awareness
  const removeSelfPresence = useCallback(async () => {
    try {
      if (!collabProvider?.document) {
        throw new Error('Missing provider yDoc');
      }
      if (!collabProvider?.awareness) {
        throw new Error('Missing provider awareness');
      }
      if (!userInfo?.uuid) {
        throw new Error('Missing user uuid');
      }
      if (!runtimeId) {
        throw new Error('Missing runtimeId');
      }
      if (isRemovingRef.current) {
        console.log('[removeSelfPresence]: Removal in progress');
        return true;
      }

      // Mark removal in progress...
      isRemovingRef.current = true;

      // Yjs awareness - remove this users presence data
      const yAwareness = collabProvider.awareness;

      // Mark self as offline / not present
      yAwareness.setLocalState(null);

      // Probably leaving page, but update state anyway
      setLastSentUpdate(null);

      return true;
    } catch (error) {
      console.error('[removeSelfPresence]:', error);
      return false;
    } finally {
      // Reset ref after attempt
      isRemovingRef.current = false;
    }
  }, [collabProvider?.awareness, collabProvider?.document, runtimeId, userInfo?.uuid]);

  /**
   * Interval and effect sets up broadcasting self
   * presence info while connected and enabled.
   *
   * Note: an initial presence state should be created and set
   * while setting up the yAwareness protocol right before
   * creating the provider, so that the first update emitted
   * contains all proper user info rather than waiting for this
   * hook to mount and then post an update.
   */
  const selfUpdateInterval = useInterval(updateSelfPresence, INTERVAL_MS, {
    autoInvoke: false,
  });

  /**
   * Effect start/stop presence update interval
   *
   * eslint disabled:
   * dont add selfUpdatePresence to the the deps array
   * since start/stop calls change the reference causing
   * the effect cleanup to run and stop the interval
   */
  useEffect(() => {
    // disabled - stop any existing interval
    if (!isEnabled) {
      selfUpdateInterval.stop();
      return;
    }

    // start on mount / enabled
    selfUpdateInterval.start();

    // cleanup unmount
    return () => {
      selfUpdateInterval.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);

  /**
   * Send one initial presence update
   * on mount when enabled and active.
   */
  const onMountRef = useRef(false);
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    if (isIdle) {
      return;
    }
    if (onMountRef.current) {
      return;
    }
    updateSelfPresence();
    onMountRef.current = true;
  }, [isEnabled, isIdle, updateSelfPresence]);

  /**
   * Even though we will be sending updates while idle,
   * when we become active again, send out one update
   * event to help rerender active status ux more quickly.
   */
  const prevIdleResumeRef = useRef(isIdle);
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const prevIdle = prevIdleResumeRef.current;
    // became active from idle
    if (prevIdle && !isIdle) {
      updateSelfPresence();
    }
    prevIdleResumeRef.current = isIdle;
  }, [isIdle, isEnabled, updateSelfPresence]);

  /**
   * Best effort cleanup on unmount / pagehide
   */
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const onPageHide = async (e: PageTransitionEvent) => {
      await removeSelfPresence();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      removeSelfPresence(); // handle unmount
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [isEnabled, removeSelfPresence]);

  // --------------------------------------------------
  // Build presence map with tiptap awareness events
  // --------------------------------------------------

  const [userPresenceMap, setUserPresenceMap] = useState<UserPresenceMap>({});

  /**
   *
   * Optionally updates the internal state
   * after building the new presence map.
   *
   * Should be called by a throttled or debounced
   * function on the outside to avoid excessive updates.
   *
   * Note: noticed that consistently the first awareness
   * update fires before the provider is synced, or seemingly
   * at a concurrent time... but, now that we are creating awareness
   * info prior to provider setup, this is less of an issue, so
   * if adding a guard like !collabProvider?.isSynced it may
   * lead to the first update being missed because the provider
   * was not yet fully synced...
   */
  const buildPresenceMap = useCallback(
    async ({ awUpdate, updateState = true }: BuildPresenceMapArgs) => {
      try {
        if (!collabProvider?.document) {
          throw new Error('Missing provider ydoc');
        }
        if (!collabProvider?.awareness) {
          throw new Error('Missing provider awareness');
        }

        const mapped = statesToPresenceMap({ awUpdate });

        if (mapped && updateState) {
          setUserPresenceMap(mapped);
        }

        return mapped;
      } catch (error) {
        console.error('[buildPresenceMap]:', error);
        throw error;
      }
    },
    [collabProvider],
  );

  const result: UseNxtDocPresenceRtn = {
    removeSelfPresence,
    updateSelfPresence,
    buildPresenceMap,
    userPresenceMap,
    setUserPresenceMap,
    lastSentUpdate,
  };

  return result;
}

/**
 * Accepts tiptap / yjs awareness update
 * and assembles the user presence map.
 *
 * Logic is extracted to a pure function outside
 * of the hook to make it easier for unit tests.
 *
 */
type StatesToPresenceMapArgs = {
  awUpdate?: AwarenessUpdateInfo;
};

export function statesToPresenceMap({
  awUpdate,
}: StatesToPresenceMapArgs): UserPresenceMap {
  try {
    // if no states, return empty object
    const awarenessStates = awUpdate?.states ?? [];
    if (awarenessStates.length === 0) {
      return {};
    }

    // Map to collate user presence info (uuid:info)
    const usersMap = new Map<UserUuid, UserPresenceInfo>();

    // Iterate awareness states
    for (const awState of awarenessStates) {
      const awUser = awState?.user;
      const awPresence = awState?.presence;
      const awUserUuid = awPresence?.uuid;
      const awRuntimeId = awPresence?.runtimeId;

      // skip user if critical data missing
      if (!awUserUuid || !awRuntimeId) {
        continue;
      }

      // Get information from awareness data
      const awDisplayName = awUser?.name ?? '';
      const awUserEmail = awUser?.email ?? '';
      const awAvatar = awUser?.avatar ?? '';
      const awColor = awUser?.color ?? '';
      const awIsReadOnly = awPresence?.isReadOnlyMode;
      const awLastActiveIso = awPresence?.lastActiveIso;

      // Get or create entry for user
      const existing = usersMap.get(awUserUuid);
      const prevSessions = existing?.sessions ?? {};

      const updated: UserPresenceInfo = {
        uuid: awUserUuid,
        email: awUserEmail,
        name: awDisplayName,
        avatar: awAvatar,
        color: awColor,
        sessions: {
          ...prevSessions,
          [awRuntimeId]: {
            runtimeId: awRuntimeId,
            isReadOnlyMode: awIsReadOnly,
            lastActiveIso: awLastActiveIso,
          },
        },
      };
      // Update the users map entry
      usersMap.set(awUserUuid, updated);
    }

    // Convert map to object for return / state
    const mapping = Object.fromEntries(usersMap);
    return mapping;
  } catch (error) {
    console.error('[statesToPresenceMap]:', error);
    throw error;
  }
}
